/**
 * Engineering Agent dock — the right-edge chat panel.
 *
 * Mounted by <EngineeringAgentProvider> at root layout level so it
 * survives route changes. State (open / agent / threadId) lives in
 * the provider, which persists to localStorage; this component is
 * just the panel UI + SSE streaming + thread browsing.
 *
 * Backend (trunk):
 *   POST /api/engineering-agents/:agent/chat
 *   GET  /api/engineering-agents/:agent/threads
 *   GET  /api/engineering-agents/:agent/threads/:threadId/messages
 *
 * Gating: server enforces @stampede.ai only. The provider also skips
 * rendering for non-stampede users (enabled=false).
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type SVGProps,
  type Dispatch,
  type SetStateAction,
} from 'react'
import { useParams } from 'react-router'
import {
  MessagesSquare,
  X,
  SendHorizontal,
  ChevronDown,
  Store,
  Clock,
  Plus,
  Paperclip,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from 'tailwind-v4/components/button'
import { NativeSelect } from 'tailwind-v4/components/native-select'
import { marked } from 'marked'
import { getToken } from '@/utils/AuthTokenHelper'
import appConfig from '@/config'
import { useSelectedVenue } from '@/state/backend/venues/venues.hooks'
import {
  DOCK_WIDTH_PX,
  useEngineeringAgent,
  type EngineeringAgentId,
} from './provider'

marked.setOptions({ gfm: true, breaks: true })

// ── Types ──────────────────────────────────────────────────────────────────

interface ToolCall {
  id: string
  name: string
  status: 'running' | 'done' | 'error'
  result?: unknown
}

interface Message {
  id: string
  role: 'user' | 'assistant'
  text: string
  toolCalls: ToolCall[]
  /** Asset URLs attached to this user message (rendered inline). */
  attachmentUrls?: string[]
}

interface Attachment {
  /** Local-only id for keying. */
  localId: string
  /** Server-side Asset row id (returned by /presign). */
  assetId: string
  /** Public HTTPS URL on the assets bucket — what the agent sees. */
  url: string
  /** Original filename. */
  filename: string
  /** MIME type. */
  mediaType: string
  /** Base64 preview URL for thumbnail (never sent to backend — local only). */
  previewDataUrl: string
}

interface ThreadSummary {
  threadId: string
  title: string
  createdAt: string
  updatedAt: string
}

// ── Agent metadata ──────────────────────────────────────────────────────────

const AGENT_OPTIONS: Record<
  EngineeringAgentId,
  {
    label: string
    placeholders: string[]
    emptyTitle: string
    emptyState: string
  }
> = {
  'engineering-bookings': {
    label: 'Engineering Agent · Bookings',
    placeholders: [
      'Why didn’t this booking get a confirmation email?',
      'Show me the recent bookings for serial XYZ on 2026-05-23',
      'Search the zion repo for where deposit refunds are processed',
      'What columns does the zion.bookings table have?',
    ],
    emptyTitle: 'Debug a reservation issue',
    emptyState:
      'The agent can read the bookings/inbox/billing databases, browse the codebase (zion, neo, morpheus, front-end-mono), and walk through what happened on a specific booking.',
  },
  'engineering-bookings-csv': {
    label: 'Engineering Agent · Bookings CSV',
    placeholders: [
      'Why are these rows blocked?',
      'Clean this upload and give me a CSV to re-import',
      'Why couldn’t row 14 be put on a table?',
      'How does the auto-allocator decide which table a booking gets?',
    ],
    emptyTitle: 'Get a bookings CSV through pre-flight',
    emptyState:
      'The agent reads this venue’s CSV import jobs, explains exactly why rows are blocked or flagged (in plain English), and hands back a cleaned CSV you can re-upload — plus it knows the zion import + table-allocation code if you want the why.',
  },
  'engineering-wifi': {
    label: 'Engineering Agent · WiFi',
    placeholders: [
      'Why is this venue’s WiFi offline?',
      'Show me the recent RADIUS auth failures for this serial',
      'Search the trinity repo for the SnapBox provisioning flow',
      'What does the trinity.sessions table look like?',
    ],
    emptyTitle: 'Debug a WiFi / network issue',
    emptyState:
      'The agent can read the trinity database, browse the Trinity + Connect codebases, and walk through what happened with a venue’s WiFi, RADIUS auth, captive portal, or device state.',
  },
  'engineering-crm': {
    label: 'Engineering Agent · CRM Front End',
    placeholders: [
      'Why is this screen blank for this customer?',
      'The list isn’t refreshing after I change something',
      'Why can’t this user see that venue?',
      'How does the org switcher load venues?',
    ],
    emptyTitle: 'CRM / frontend — or anything general',
    emptyState:
      'The general Connect agent — the CRM web app (screens, state, RTK Query, routing, PWA) + morpheus (auth/orgs/users). Also the catch-all when your question doesn’t fit a specialist agent.',
  },
  'engineering-marketing': {
    label: 'Engineering Agent · Marketing',
    placeholders: [
      'Why didn’t this campaign send?',
      'This audience count looks wrong',
      'The birthday automation isn’t firing',
      'How does a workflow trigger step through its nodes?',
    ],
    emptyTitle: 'Debug a marketing issue',
    emptyState:
      'The agent covers email/SMS campaigns, segments/audiences, and workflows — the service (Laravel) backend, morpheus segments/interactions, and the marketing UI. It can query the service DB directly.',
  },
  'engineering-loyalty': {
    label: 'Engineering Agent · Loyalty',
    placeholders: [
      'Why didn’t this customer get their points?',
      'This reward won’t redeem',
      'The member’s wallet pass isn’t updating',
      'Why isn’t the Wi-Fi automation awarding points?',
    ],
    emptyTitle: 'Debug a loyalty issue',
    emptyState:
      'The agent covers points, rewards, vouchers and Apple/Google passes — the loyalty backend, its DB, and the automations that award points off customer activity.',
  },
  'engineering-reviews': {
    label: 'Engineering Agent · Reviews',
    placeholders: [
      'Why didn’t this customer get a review request?',
      'This review isn’t showing up',
      'The rating score looks wrong',
      'Is Google review syncing working?',
    ],
    emptyTitle: 'Debug a reviews issue',
    emptyState:
      'The agent covers review collection, review-request emails, AI replies, Google/TripAdvisor sync, and rating metrics — the tank backend + its DB.',
  },
}

// ── Component ──────────────────────────────────────────────────────────────

export function EngineeringAgentDock() {
  const { isOpen, agent, threadId, scope, setAgent, setThreadId, close } =
    useEngineeringAgent()

  // Read URL params reactively so the agent always has the current venue
  // context regardless of which page the user navigated to.
  const { org_id, serial } = useParams<{ org_id?: string; serial?: string }>()
  const { data: venue } = useSelectedVenue(serial ?? '')

  // Context handed to the agent. Merges:
  //   - explicit `scope` from the entry point (e.g. a loyalty scheme page
  //     passing schemeId, or a venue reviews page passing serial), and
  //   - URL-derived venue context (serial from /venues/:serial/*).
  // Explicit scope wins on overlap. Null only when we have neither.
  const venueContext = useMemo(() => {
    const effectiveSerial = scope?.serial ?? serial ?? null
    const hasAnything =
      Boolean(org_id) && (Boolean(effectiveSerial) || Boolean(scope?.schemeId))
    if (!hasAnything) return null
    return {
      orgId: org_id,
      serial: effectiveSerial,
      venueName: scope?.venueName ?? venue?.alias ?? venue?.name ?? null,
      configId: null,
      schemeId: scope?.schemeId ?? null,
      schemeName: scope?.schemeName ?? null,
      importJobId: scope?.importJobId ?? null,
    }
  }, [
    org_id,
    serial,
    venue?.alias,
    venue?.name,
    scope?.serial,
    scope?.schemeId,
    scope?.schemeName,
    scope?.venueName,
    scope?.importJobId,
  ])

  const [threads, setThreads] = useState<ThreadSummary[]>([])
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [isLoadingThread, setIsLoadingThread] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [threadsOpen, setThreadsOpen] = useState(false)
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [uploadingCount, setUploadingCount] = useState(0)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const abortRef = useRef<AbortController | null>(null)
  const threadsPanelRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const agentMeta = AGENT_OPTIONS[agent]
  const placeholder = useMemo(
    () =>
      agentMeta.placeholders[
        Math.floor(Math.random() * agentMeta.placeholders.length)
      ],
    [agentMeta]
  )

  // Close threads popover on outside click
  useEffect(() => {
    if (!threadsOpen) return
    function onDown(e: MouseEvent) {
      if (!threadsPanelRef.current?.contains(e.target as Node)) {
        setThreadsOpen(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => {
      document.removeEventListener('mousedown', onDown)
    }
  }, [threadsOpen])

  // Reload thread list whenever the agent changes (or the panel opens).
  // We don't reset messages here — provider's threadId restoration via
  // localStorage might want us to load a specific thread next.
  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    setThreads([])
    setError(null)
    void fetchTrunkJson(`/api/engineering-agents/${agent}/threads`)
      .then((data) => {
        if (!cancelled) setThreads(data.threads as ThreadSummary[])
      })
      .catch((err: Error) => {
        if (!cancelled) setError(`Failed to load threads: ${err.message}`)
      })
    return () => {
      cancelled = true
    }
  }, [agent, isOpen])

  // When threadId or agent changes (incl. on first mount), load that thread's
  // messages. Empty threadId → blank conversation.
  //
  // CRITICAL: clear `isLoadingThread` on every effect entry — including the
  // early-return paths. A previous fetch may have left it true; if the new
  // effect bails before its own .finally runs (no threadId, dock closed),
  // there's no other code path to clear it and the panel sticks on
  // "Loading thread…" forever.
  //
  // Streaming guard: during a live `send()` stream, the SSE handler calls
  // setThreadId() to lock in the server-resolved id. That triggers this
  // effect — without the guard it would wipe the in-flight conversation and
  // refetch an empty thread server-side. `streaming` isn't in the deps
  // (we don't WANT this effect to fire when streaming flips), but the
  // closure picks up the latest value at re-render time, which is enough.
  useEffect(() => {
    if (streaming) return
    if (!isOpen || !threadId) {
      setMessages([])
      setIsLoadingThread(false)
      return
    }
    let cancelled = false
    setMessages([])
    setIsLoadingThread(true)
    setError(null)
    void fetchTrunkJson(
      `/api/engineering-agents/${agent}/threads/${threadId}/messages`
    )
      .then((data) => {
        if (cancelled) return
        const raw = data.messages as {
          id: string
          role: string
          content: unknown
        }[]
        setMessages(buildMessagesFromHistory(raw, threadId))
      })
      .catch((err: Error) => {
        if (!cancelled) {
          // 404 on a stale localStorage threadId is expected — just clear it.
          if (err.message.startsWith('404')) {
            setThreadId(null)
          } else {
            setError(`Failed to load thread: ${err.message}`)
          }
          setMessages([])
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingThread(false)
      })
    return () => {
      cancelled = true
    }
  }, [agent, threadId, isOpen, setThreadId])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, streaming, isLoadingThread])

  // Stop any in-flight stream when the dock unmounts.
  useEffect(() => {
    return () => {
      abortRef.current?.abort()
    }
  }, [])

  const refreshThreads = useCallback(async () => {
    try {
      const data = await fetchTrunkJson(
        `/api/engineering-agents/${agent}/threads`
      )
      setThreads(data.threads as ThreadSummary[])
    } catch {
      /* non-fatal */
    }
  }, [agent])

  const newChat = useCallback(() => {
    setThreadId(null)
    setMessages([])
    setError(null)
    setThreadsOpen(false)
    setAttachments([])
  }, [setThreadId])

  // ── Asset upload (presign → S3 PUT → confirm) ────────────────────────────
  //
  // The assets/presign route requires the Thread row to exist server-side.
  // The dock doesn't create that row until the user sends their first chat
  // message — so we gate the attach button on `threadId` being non-null
  // (i.e. there's at least one turn in this conversation already). No
  // separate "thread-init" handshake needed; the upload flow becomes a
  // straight presign → PUT → confirm.

  const uploadFile = useCallback(
    async (file: File): Promise<Attachment> => {
      if (!org_id) {
        throw new Error(
          'No org context — open the dock from a venue page first.'
        )
      }
      if (!threadId) {
        throw new Error(
          'Send a message first to start the conversation, then attach images.'
        )
      }

      const token = getToken()?.accessToken ?? ''
      const presignRes = await fetch(
        `${appConfig.url.trunk}/organisations/${encodeURIComponent(org_id)}/assets/presign`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            threadId,
            filename: file.name,
            mimeType: file.type,
            size: file.size,
          }),
        }
      )
      if (!presignRes.ok) {
        const text = await presignRes.text().catch(() => '')
        throw new Error(
          `presign failed (${presignRes.status}): ${text.slice(0, 200)}`
        )
      }
      const presign = (await presignRes.json()) as {
        assetId: string
        uploadUrl: string
        fileUrl: string
      }

      // PUT the raw bytes to S3 directly. Same flow the main chat upload uses.
      const putRes = await fetch(presign.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      })
      if (!putRes.ok) {
        throw new Error(`S3 PUT failed (${putRes.status})`)
      }

      await fetch(
        `${appConfig.url.trunk}/organisations/${encodeURIComponent(
          org_id
        )}/assets/${encodeURIComponent(presign.assetId)}/confirm`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      )

      const previewDataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          resolve(reader.result as string)
        }
        reader.onerror = () => {
          reject(reader.error)
        }
        reader.readAsDataURL(file)
      })

      return {
        localId: `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        assetId: presign.assetId,
        url: presign.fileUrl,
        filename: file.name,
        mediaType: file.type,
        previewDataUrl,
      }
    },
    [org_id, threadId]
  )

  const addFiles = useCallback(
    async (rawFiles: File[]) => {
      const files = rawFiles.filter((f) =>
        /^image\/(png|jpe?g|gif|webp)$/.test(f.type)
      )
      if (files.length === 0) return

      setError(null)
      setUploadingCount((c) => c + files.length)
      try {
        const settled = await Promise.allSettled(
          files.map((f) => uploadFile(f))
        )
        for (const r of settled) {
          if (r.status === 'fulfilled') {
            setAttachments((prev) => [...prev, r.value])
          } else {
            setError(
              `Upload failed: ${r.reason instanceof Error ? r.reason.message : String(r.reason)}`
            )
          }
        }
      } finally {
        setUploadingCount((c) => Math.max(0, c - files.length))
      }
    },
    [uploadFile]
  )

  const onFilePickerChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? [])
      e.target.value = '' // allow re-selecting the same file
      void addFiles(files)
    },
    [addFiles]
  )

  const onPaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const items = Array.from(e.clipboardData?.items ?? [])
      const imageItems = items.filter(
        (it) => it.kind === 'file' && it.type.startsWith('image/')
      )
      if (imageItems.length === 0) return
      // Same gate as the attach button: thread must exist server-side before
      // we can presign an upload. Let plain-text paste through normally.
      if (!threadId || !org_id) return
      e.preventDefault()
      const files = imageItems
        .map((it) => it.getAsFile())
        .filter((f): f is File => Boolean(f))
      void addFiles(files)
    },
    [addFiles, threadId, org_id]
  )

  const removeAttachment = useCallback((localId: string) => {
    setAttachments((prev) => prev.filter((a) => a.localId !== localId))
  }, [])

  // Auto-grow the textarea up to a max height; let it scroll inside after that.
  useEffect(() => {
    const ta = textareaRef.current
    if (!ta) return
    ta.style.height = 'auto'
    ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`
  }, [input])

  const send = useCallback(async () => {
    const userText = input.trim()
    // Allow sending if there's text OR pending attachments. Not both empty.
    if ((!userText && attachments.length === 0) || streaming) return
    if (uploadingCount > 0) return // wait for in-flight uploads
    setInput('')
    setError(null)

    const turnAttachments = attachments
    setAttachments([])

    const userMsg: Message = {
      id: `u-${Date.now()}`,
      role: 'user',
      text: userText,
      toolCalls: [],
      attachmentUrls: turnAttachments.map((a) => a.url),
    }
    const assistantMsg: Message = {
      id: `a-${Date.now()}`,
      role: 'assistant',
      text: '',
      toolCalls: [],
    }
    setMessages((prev) => [...prev, userMsg, assistantMsg])
    setStreaming(true)

    // Generate a fresh threadId for a brand-new chat. Server's
    // ensureThreadOwnership binds it to this agent.
    const tid = threadId ?? freshThreadId()
    const controller = new AbortController()
    abortRef.current = controller

    try {
      const token = getToken()?.accessToken ?? ''
      const res = await fetch(
        `${appConfig.url.trunk}/api/engineering-agents/${agent}/chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            messages: [
              { role: 'user', content: userText || '(image attached)' },
            ],
            orgId: org_id,
            threadId: tid,
            venueContext,
            attachments: turnAttachments.map((a) => ({
              url: a.url,
              filename: a.filename,
              mediaType: a.mediaType,
            })),
          }),
          signal: controller.signal,
        }
      )

      if (!res.ok || !res.body) {
        const body = await res.text().catch(() => '')
        setError(`Server ${res.status}: ${body.slice(0, 200)}`)
        setStreaming(false)
        return
      }

      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        let idx: number
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, idx)
          buffer = buffer.slice(idx + 2)
          handleFrame(frame, setMessages, setError, setThreadId)
        }
      }

      // Lock in the threadId locally (server may have echoed via SSE,
      // but we set it here as a fallback in case the `thread` event was
      // missed somehow).
      if (!threadId) setThreadId(tid)
      void refreshThreads()
    } catch (err) {
      if ((err as Error).name === 'AbortError') return
      setError(err instanceof Error ? err.message : 'Network error')
    } finally {
      setStreaming(false)
      abortRef.current = null
    }
  }, [
    agent,
    input,
    streaming,
    threadId,
    setThreadId,
    org_id,
    venueContext,
    refreshThreads,
    attachments,
    uploadingCount,
  ])

  const stop = useCallback(() => {
    abortRef.current?.abort()
    setStreaming(false)
  }, [])

  const loadThread = useCallback(
    (id: string) => {
      setThreadId(id)
      setThreadsOpen(false)
    },
    [setThreadId]
  )

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <aside
      className={cn(
        'new-ui-theme fixed inset-y-0 right-0 z-[9999]',
        'flex flex-col overflow-hidden',
        'max-w-[100vw] border-l border-border bg-popover text-foreground',
        'transform-gpu transition-transform duration-100 ease-out',
        isOpen ? 'translate-x-0' : 'pointer-events-none translate-x-full'
      )}
      style={{ width: DOCK_WIDTH_PX }}
      aria-hidden={!isOpen}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-foreground">
            <CraneIcon className="size-4" />
          </div>
          <div className="flex min-w-0 flex-col gap-1">
            <NativeSelect
              size="sm"
              value={agent}
              onChange={(e) => {
                setAgent(e.target.value as EngineeringAgentId)
              }}
              disabled={streaming}
              className="w-full max-w-[240px]"
              aria-label="Pick an Engineering Agent"
            >
              {(Object.keys(AGENT_OPTIONS) as EngineeringAgentId[]).map((a) => (
                <option key={a} value={a}>
                  {AGENT_OPTIONS[a].label}
                </option>
              ))}
            </NativeSelect>
            <p className="text-xs text-muted-foreground">@stampede.ai only</p>
          </div>
        </div>

        <div className="relative flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => {
              setThreadsOpen((v) => !v)
            }}
            aria-label="Recent threads"
            title="Recent threads"
          >
            <Clock className="size-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={newChat}
            aria-label="New chat"
            title="New chat"
          >
            <Plus className="size-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={close}
            aria-label="Close Engineering Agent"
          >
            <X className="size-4" />
          </Button>

          {threadsOpen ? (
            <div
              ref={threadsPanelRef}
              className="absolute right-0 top-9 z-30 w-72 overflow-hidden rounded-xl border border-border bg-popover"
            >
              <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
                <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Recent threads
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  onClick={newChat}
                  leftIcon={<Plus className="size-3" />}
                >
                  New chat
                </Button>
              </div>
              <ul className="flex max-h-72 flex-col overflow-y-auto">
                {threads.length === 0 ? (
                  <li className="px-3 py-3 text-xs text-muted-foreground">
                    No threads yet with this agent.
                  </li>
                ) : (
                  threads.map((t) => (
                    <li
                      key={t.threadId}
                      onClick={() => {
                        loadThread(t.threadId)
                      }}
                      className={cn(
                        'flex cursor-pointer flex-col gap-1 border-b border-border px-3 py-2 text-xs last:border-b-0',
                        threadId === t.threadId
                          ? 'bg-accent text-foreground'
                          : 'text-foreground/80 hover:bg-accent/50'
                      )}
                      title={`${t.title}\nUpdated ${new Date(t.updatedAt).toLocaleString()}`}
                    >
                      <div className="truncate font-medium">
                        {t.title || '(untitled)'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {relativeTime(t.updatedAt)}
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </div>
          ) : null}
        </div>
      </div>

      {/* Context bar — shows whatever scope the agent has (scheme or venue) */}
      {venueContext && (venueContext.schemeId || venueContext.serial) ? (
        <div className="flex items-center gap-2 border-b border-border bg-muted px-4 py-2 text-xs">
          <Store className="size-4 shrink-0 text-muted-foreground" />
          {venueContext.schemeId ? (
            <>
              <span className="truncate font-medium text-foreground">
                {venueContext.schemeName ?? 'Loyalty scheme'}
              </span>
              <span className="truncate font-mono text-muted-foreground">
                {venueContext.schemeId}
              </span>
            </>
          ) : (
            <>
              <span className="truncate font-medium text-foreground">
                {venueContext.venueName ?? venueContext.serial}
              </span>
              <span className="truncate font-mono text-muted-foreground">
                {venueContext.serial}
              </span>
            </>
          )}
        </div>
      ) : null}

      {/* Messages */}
      <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-4 scroll-py-4">
        {isLoadingThread ? (
          <div className="py-8 text-center text-xs text-muted-foreground">
            Loading thread…
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4 text-center">
            <MessagesSquare className="size-6 text-muted-foreground" />
            <div className="flex flex-col gap-2">
              <p className="font-medium text-foreground">
                {agentMeta.emptyTitle}
              </p>
              <p className="text-xs leading-relaxed text-muted-foreground">
                {agentMeta.emptyState}
              </p>
            </div>
          </div>
        ) : (
          messages.map((m) => <MessageRow key={m.id} message={m} />)
        )}
        {error ? (
          <div className="rounded-lg border border-destructive/25 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        ) : null}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex flex-col gap-2 border-t border-border p-3">
        {/* Pending attachments preview */}
        {attachments.length > 0 || uploadingCount > 0 ? (
          <div className="flex flex-wrap gap-2">
            {attachments.map((a) => (
              <div key={a.localId} className="relative size-14">
                <img
                  src={a.previewDataUrl}
                  alt={a.filename}
                  title={a.filename}
                  className="size-14 rounded-lg border border-border object-cover"
                />
                <button
                  type="button"
                  onClick={() => {
                    removeAttachment(a.localId)
                  }}
                  className="absolute -right-1.5 -top-1.5 flex size-5 items-center justify-center rounded-full bg-foreground text-background"
                  aria-label={`Remove ${a.filename}`}
                >
                  <X className="size-3" />
                </button>
              </div>
            ))}
            {Array.from({ length: uploadingCount }).map((_, i) => (
              <div
                key={`up-${i}`}
                className="flex size-14 items-center justify-center rounded-lg border border-dashed border-border bg-muted text-xs text-muted-foreground"
              >
                …
              </div>
            ))}
          </div>
        ) : null}
        <form
          onSubmit={(e) => {
            e.preventDefault()
            void send()
          }}
          className="flex items-end gap-2"
        >
          {/* Attach button only after a thread exists (i.e. user has sent at
              least one message). The presign endpoint needs the Thread row
              to exist server-side, so hide the affordance until it's safe. */}
          {threadId && org_id ? (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                multiple
                onChange={onFilePickerChange}
                className="hidden"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => fileInputRef.current?.click()}
                disabled={streaming || isLoadingThread}
                title="Attach image"
                aria-label="Attach image"
                className="shrink-0"
              >
                <Paperclip className="size-4" />
              </Button>
            </>
          ) : null}
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                if (!streaming && !isLoadingThread && uploadingCount === 0) {
                  void send()
                }
              }
            }}
            onPaste={onPaste}
            placeholder={placeholder}
            disabled={streaming || isLoadingThread}
            rows={1}
            className="flex-1 resize-none overflow-y-auto rounded-lg border border-border bg-background px-3 py-2 text-sm leading-snug text-foreground outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:opacity-50"
            style={{ maxHeight: 200 }}
          />
          {streaming ? (
            <Button
              type="button"
              variant="destructive"
              onClick={stop}
              className="shrink-0"
            >
              Stop
            </Button>
          ) : (
            <Button
              type="submit"
              variant="primary"
              size="icon"
              disabled={
                (!input.trim() && attachments.length === 0) ||
                uploadingCount > 0
              }
              aria-label="Send"
              className="shrink-0"
            >
              <SendHorizontal className="size-4" />
            </Button>
          )}
        </form>
      </div>
    </aside>
  )
}

// ── SSE frame handling ─────────────────────────────────────────────────────

function handleFrame(
  frame: string,
  setMessages: Dispatch<SetStateAction<Message[]>>,
  setError: Dispatch<SetStateAction<string | null>>,
  setThreadId: (id: string | null) => void
) {
  const lines = frame.split('\n')
  const eventLine = lines.find((l) => l.startsWith('event:'))
  const dataLine = lines.find((l) => l.startsWith('data:'))
  if (!dataLine) return

  const eventType = eventLine?.slice(6).trim() ?? ''
  const data = dataLine.slice(5).trim()
  if (!data) return

  let parsed: Record<string, unknown>
  try {
    parsed = JSON.parse(data)
  } catch {
    return
  }

  if (eventType === 'thread') {
    const id = typeof parsed.threadId === 'string' ? parsed.threadId : null
    if (id) setThreadId(id)
    return
  }

  const payload =
    parsed.payload && typeof parsed.payload === 'object'
      ? (parsed.payload as Record<string, unknown>)
      : parsed

  const textChunk =
    (typeof payload.text === 'string' && payload.text) ||
    (typeof payload.delta === 'string' && payload.delta) ||
    (typeof payload.textDelta === 'string' && payload.textDelta) ||
    ''

  if (
    (eventType === 'text-delta' ||
      eventType === 'text' ||
      eventType === 'message-delta') &&
    textChunk
  ) {
    setMessages((prev) => {
      const next = [...prev]
      const last = next[next.length - 1]
      if (last && last.role === 'assistant') {
        next[next.length - 1] = { ...last, text: last.text + textChunk }
      }
      return next
    })
    return
  }

  if (eventType === 'reasoning-delta' || eventType === 'reasoning') return

  if (
    eventType === 'tool-call' ||
    eventType === 'tool-input-start' ||
    eventType === 'tool-input-streaming' ||
    eventType === 'tool-input-delta'
  ) {
    const toolName = (payload.toolName ?? payload.name ?? 'tool') as string
    const toolCallId = (payload.toolCallId ??
      payload.id ??
      `tc-${Date.now()}`) as string
    setMessages((prev) => upsertToolCall(prev, toolCallId, toolName, 'running'))
    return
  }

  if (
    eventType === 'tool-result' ||
    eventType === 'tool-output-available' ||
    eventType === 'tool-output'
  ) {
    const toolCallId = (payload.toolCallId ?? payload.id ?? '') as string
    const result = payload.output ?? payload.result
    setMessages((prev) =>
      upsertToolCall(prev, toolCallId, undefined, 'done', result)
    )
    return
  }

  if (eventType === 'error') {
    const message =
      typeof parsed.message === 'string' ? parsed.message : 'Stream error'
    setError(message)
    return
  }

  const QUIET: ReadonlySet<string> = new Set([
    'start',
    'start-step',
    'step-finish',
    'finish',
    'finish-step',
    'finish-reason',
    'done',
    'message',
    'message-start',
    'message-end',
    'response-metadata',
  ])
  if (!QUIET.has(eventType)) {
    console.debug('[engineering-agent] unhandled SSE event', eventType, parsed)
  }
}

function upsertToolCall(
  messages: Message[],
  id: string,
  name: string | undefined,
  status: ToolCall['status'],
  result?: unknown
): Message[] {
  const next = [...messages]
  const last = next[next.length - 1]
  if (!last || last.role !== 'assistant') return next

  const existing = last.toolCalls.find((tc) => tc.id === id)
  if (existing) {
    next[next.length - 1] = {
      ...last,
      toolCalls: last.toolCalls.map((tc) =>
        tc.id === id
          ? {
              ...tc,
              status,
              result: result ?? tc.result,
              name: name ?? tc.name,
            }
          : tc
      ),
    }
  } else {
    next[next.length - 1] = {
      ...last,
      toolCalls: [
        ...last.toolCalls,
        { id, name: name ?? 'tool', status, result },
      ],
    }
  }
  return next
}

// ── Subcomponents ──────────────────────────────────────────────────────────

function MessageRow({ message }: { message: Message }) {
  const empty =
    !message.text &&
    message.toolCalls.length === 0 &&
    message.role === 'assistant'

  return (
    <div
      className={cn(
        'flex flex-col gap-2',
        message.role === 'user' ? 'items-end' : 'items-start'
      )}
    >
      {message.toolCalls.length > 0 ? (
        <ToolActivityPill toolCalls={message.toolCalls} />
      ) : null}

      {/* Image attachments for user messages — open full-size in a new tab on click. */}
      {message.role === 'user' && message.attachmentUrls?.length ? (
        <div className="flex max-w-[85%] flex-wrap justify-end gap-2">
          {message.attachmentUrls.map((u) => (
            <a
              key={u}
              href={u}
              target="_blank"
              rel="noreferrer"
              className="block size-20 overflow-hidden rounded-lg border border-border"
            >
              <img src={u} alt="" className="size-full object-cover" />
            </a>
          ))}
        </div>
      ) : null}

      {message.text || message.role === 'user' ? (
        <div
          className={cn(
            'max-w-[85%] break-words rounded-xl px-3 py-2 text-sm text-foreground',
            message.role === 'user'
              ? 'whitespace-pre-wrap bg-accent'
              : 'bg-muted'
          )}
        >
          {message.role === 'assistant' ? (
            <MarkdownBody source={message.text} />
          ) : (
            message.text
          )}
        </div>
      ) : null}
      {empty ? (
        <div className="text-xs italic text-muted-foreground">Thinking…</div>
      ) : null}
    </div>
  )
}

function ToolActivityPill({ toolCalls }: { toolCalls: ToolCall[] }) {
  const [expanded, setExpanded] = useState(false)

  const active = toolCalls.find((tc) => tc.status === 'running')
  const headline = active ?? toolCalls[toolCalls.length - 1]
  const anyRunning = Boolean(active)
  const anyError = toolCalls.some((tc) => tc.status === 'error')

  let headlineDot = 'bg-success'
  if (anyRunning) {
    headlineDot = 'bg-warning motion-safe:animate-pulse'
  } else if (anyError) {
    headlineDot = 'bg-destructive'
  }

  const callCountLabel = anyRunning
    ? 'running…'
    : `${toolCalls.length} call${toolCalls.length === 1 ? '' : 's'}`

  return (
    <div className="w-full max-w-[85%] overflow-hidden rounded-lg border border-border">
      <button
        type="button"
        onClick={() => {
          setExpanded((v) => !v)
        }}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-accent"
      >
        <span className={cn('size-1.5 shrink-0 rounded-full', headlineDot)} />
        <span className="truncate font-mono text-foreground">
          {headline?.name ?? 'tool'}
        </span>
        {toolCalls.length > 1 ? (
          <span className="shrink-0 text-xs text-muted-foreground">
            +{toolCalls.length - 1} more
          </span>
        ) : null}
        <span className="ml-auto shrink-0 text-muted-foreground">
          {callCountLabel}
        </span>
        <ChevronDown
          className={cn(
            'size-4 shrink-0 text-muted-foreground transition-transform duration-100',
            expanded && 'rotate-180'
          )}
        />
      </button>
      {expanded ? (
        <div className="divide-y divide-border border-t border-border">
          {toolCalls.map((tc) => (
            <ToolCallRow key={tc.id} toolCall={tc} />
          ))}
        </div>
      ) : null}
    </div>
  )
}

function MarkdownBody({ source }: { source: string }) {
  const html = useMemo(() => marked.parse(source, { async: false }), [source])
  return (
    <div
      className={cn(
        'text-sm leading-relaxed text-foreground',
        '[&>p]:my-1.5 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0',
        '[&>ul]:my-1.5 [&>ol]:my-1.5 [&>ul]:pl-5 [&>ol]:pl-5',
        '[&>ul]:list-disc [&>ol]:list-decimal',
        '[&_li]:my-0.5',
        '[&>h1]:text-base [&>h1]:font-semibold [&>h1]:mt-3 [&>h1]:mb-1.5',
        '[&>h2]:text-sm [&>h2]:font-semibold [&>h2]:mt-3 [&>h2]:mb-1.5',
        '[&>h3]:text-sm [&>h3]:font-semibold [&>h3]:mt-2.5 [&>h3]:mb-1',
        '[&>h4]:text-xs [&>h4]:font-semibold [&>h4]:uppercase [&>h4]:tracking-wide [&>h4]:mt-2 [&>h4]:mb-1',
        '[&_strong]:font-semibold',
        '[&_em]:italic',
        '[&_a]:underline [&_a]:underline-offset-2',
        '[&_code]:font-mono [&_code]:text-xs [&_code]:rounded [&_code]:px-1 [&_code]:py-px',
        '[&_code]:bg-foreground/10',
        '[&>pre]:my-2 [&>pre]:rounded-md [&>pre]:p-2.5 [&>pre]:overflow-x-auto',
        '[&>pre]:bg-foreground/5',
        '[&>pre]:text-xs [&>pre]:leading-snug',
        '[&>pre>code]:bg-transparent',
        '[&>pre>code]:p-0 [&>pre>code]:text-xs',
        '[&>blockquote]:border-l-2 [&>blockquote]:pl-3 [&>blockquote]:my-1.5',
        '[&>blockquote]:border-foreground/20',
        '[&>blockquote]:text-muted-foreground',
        '[&>table]:my-2 [&>table]:w-full [&>table]:text-xs [&>table]:border-collapse',
        '[&_th]:text-left [&_th]:font-semibold [&_th]:px-2 [&_th]:py-1',
        '[&_td]:px-2 [&_td]:py-1',
        '[&_thead]:border-b [&_thead]:border-border',
        '[&_tbody_tr]:border-b [&_tbody_tr]:border-border',
        '[&>hr]:my-3 [&>hr]:border-border'
      )}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

function ToolCallRow({ toolCall }: { toolCall: ToolCall }) {
  const [expanded, setExpanded] = useState(false)
  const hasResult = toolCall.result !== undefined && toolCall.result !== null

  const resultText =
    typeof toolCall.result === 'string'
      ? toolCall.result
      : JSON.stringify(toolCall.result, null, 2)

  return (
    <div className="text-xs">
      <button
        type="button"
        onClick={() => {
          if (hasResult) setExpanded((v) => !v)
        }}
        disabled={!hasResult}
        className={cn(
          'flex w-full items-center gap-2 px-3 py-2 text-left',
          hasResult && 'cursor-pointer hover:bg-accent'
        )}
      >
        <span
          className={cn(
            'size-1.5 shrink-0 rounded-full',
            toolCall.status === 'running' &&
              'bg-warning motion-safe:animate-pulse',
            toolCall.status === 'done' && 'bg-success',
            toolCall.status === 'error' && 'bg-destructive'
          )}
        />
        <span className="truncate font-mono text-foreground">
          {toolCall.name}
        </span>
        <span className="ml-auto shrink-0 text-muted-foreground">
          {toolCall.status === 'running' ? '…' : toolCall.status}
        </span>
      </button>
      {expanded && hasResult ? (
        <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all bg-foreground/5 px-3 py-2 font-mono text-xs text-muted-foreground">
          {resultText}
        </pre>
      ) : null}
    </div>
  )
}

// ── Icon ───────────────────────────────────────────────────────────────────

function CraneIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      <path d="M3.5 21h6" />
      <path d="M6.5 21V5" />
      <path d="M3 5h17" />
      <path d="M6.5 2l9.5 3" />
      <path d="M16.5 5v6.5" />
      <path d="M15.5 11.5h2" />
    </svg>
  )
}

// ── Helpers ────────────────────────────────────────────────────────────────

function freshThreadId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `thread-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

async function fetchTrunkJson(path: string): Promise<Record<string, unknown>> {
  const token = getToken()?.accessToken ?? ''
  const res = await fetch(`${appConfig.url.trunk}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`${res.status} ${text.slice(0, 200)}`)
  }
  return res.json() as Promise<Record<string, unknown>>
}

/**
 * Mastra persists message content as `MastraMessageContentV2`:
 *
 *   {
 *     format: 2,
 *     parts: [
 *       { type: 'text', text: '…' },
 *       { type: 'tool-invocation', toolInvocation: { toolCallId, toolName, state, args, result, errorText } },
 *       { type: 'reasoning', reasoning: '…' },
 *       { type: 'file', mimeType, data, url },
 *       { type: 'step-start', model },
 *       …
 *     ],
 *     experimental_attachments?: [{ name, contentType, url }],   // ← images uploaded by the user
 *     metadata?: …
 *   }
 *
 * Older data may store `content` as a bare array of parts, or as a plain
 * string. The helpers below normalise across all three shapes.
 */

function getParts(content: unknown): unknown[] {
  if (Array.isArray(content)) return content
  if (typeof content === 'object' && content !== null) {
    const c = content as { parts?: unknown }
    if (Array.isArray(c.parts)) return c.parts
  }
  return []
}

function getAttachments(content: unknown): unknown[] {
  if (typeof content === 'object' && content !== null) {
    const c = content as { experimental_attachments?: unknown }
    if (Array.isArray(c.experimental_attachments)) {
      return c.experimental_attachments
    }
  }
  return []
}

function extractText(content: unknown): string {
  if (typeof content === 'string') return content
  const parts = getParts(content)
  if (parts.length === 0) return ''
  let out = ''
  for (const part of parts) {
    if (typeof part !== 'object' || part === null) continue
    const p = part as { type?: unknown; text?: unknown }
    if (p.type === 'text' && typeof p.text === 'string') {
      out += p.text
    }
  }
  return out
}

function extractAttachmentUrls(content: unknown): string[] {
  const urls: string[] = []

  // 1. experimental_attachments (the canonical home in AI SDK v4 / Mastra v2)
  for (const att of getAttachments(content)) {
    if (typeof att !== 'object' || att === null) continue
    const a = att as { url?: unknown; contentType?: unknown }
    if (typeof a.url === 'string' && /^https?:/.test(a.url)) urls.push(a.url)
  }

  // 2. inline parts (image / image_url / file with image mime type)
  for (const part of getParts(content)) {
    if (typeof part !== 'object' || part === null) continue
    const p = part as {
      type?: unknown
      image?: unknown
      url?: unknown
      mimeType?: unknown
      mediaType?: unknown
      source?: { url?: unknown }
    }
    const type = p.type
    const isImagePart =
      type === 'image' ||
      type === 'image_url' ||
      (type === 'file' &&
        typeof (p.mimeType ?? p.mediaType) === 'string' &&
        ((p.mimeType ?? p.mediaType) as string).startsWith('image/'))
    if (!isImagePart) continue
    const url =
      (typeof p.image === 'string' && p.image) ||
      (typeof p.url === 'string' && p.url) ||
      (typeof p.source?.url === 'string' && p.source.url) ||
      null
    if (url && /^https?:/.test(url)) urls.push(url)
  }
  return urls
}

function extractToolCalls(content: unknown): ToolCall[] {
  const out: ToolCall[] = []
  for (const part of getParts(content)) {
    if (typeof part !== 'object' || part === null) continue
    const p = part as {
      type?: unknown
      toolInvocation?: {
        toolCallId?: unknown
        toolName?: unknown
        state?: unknown
        result?: unknown
        errorText?: unknown
      }
      // Older / streaming shapes also seen in the wild:
      toolCallId?: unknown
      toolName?: unknown
      name?: unknown
      result?: unknown
      output?: unknown
    }

    if (p.type === 'tool-invocation' && p.toolInvocation) {
      const ti = p.toolInvocation
      const id = typeof ti.toolCallId === 'string' ? ti.toolCallId : null
      if (!id) continue
      const name = typeof ti.toolName === 'string' ? ti.toolName : 'tool'
      const isError =
        ti.state === 'output-error' || typeof ti.errorText === 'string'
      const status: ToolCall['status'] =
        ti.state === 'result' || ti.state === 'output-available'
          ? 'done'
          : isError
            ? 'error'
            : 'running'
      out.push({ id, name, status, result: ti.result })
      continue
    }

    // Legacy inline tool-call / tool-result parts (pre-v2 shape, still
    // possible for some historical rows).
    if (p.type === 'tool-call' || p.type === 'tool-input-start') {
      const id = typeof p.toolCallId === 'string' ? p.toolCallId : null
      if (!id) continue
      const name =
        (typeof p.toolName === 'string' && p.toolName) ||
        (typeof p.name === 'string' && p.name) ||
        'tool'
      out.push({ id, name, status: 'running' })
    } else if (p.type === 'tool-result' || p.type === 'tool-output-available') {
      const id = typeof p.toolCallId === 'string' ? p.toolCallId : null
      if (!id) continue
      const idx = out.findIndex((tc) => tc.id === id)
      const result = p.output ?? p.result
      if (idx >= 0) {
        out[idx] = { ...out[idx], status: 'done', result }
      } else {
        out.push({ id, name: 'tool', status: 'done', result })
      }
    }
  }
  return out
}

/** Apply tool-result content parts from a `role: 'tool'` message onto the
 *  most recent assistant Message in the list. */
function applyToolResults(assistant: Message, content: unknown): Message {
  if (!Array.isArray(content)) return assistant
  const updated = { ...assistant, toolCalls: [...assistant.toolCalls] }
  for (const part of content) {
    if (typeof part !== 'object' || part === null) continue
    const p = part as {
      type?: unknown
      toolCallId?: unknown
      result?: unknown
      output?: unknown
    }
    if (
      p.type !== 'tool-result' &&
      p.type !== 'tool-output-available' &&
      p.type !== 'tool-output'
    ) {
      continue
    }
    const id = typeof p.toolCallId === 'string' ? p.toolCallId : null
    if (!id) continue
    const result = p.output ?? p.result
    const idx = updated.toolCalls.findIndex((tc) => tc.id === id)
    if (idx >= 0) {
      updated.toolCalls[idx] = {
        ...updated.toolCalls[idx],
        result,
        status: 'done',
      }
    } else {
      // tool-result without a matching tool-call — surface it anyway so the
      // user sees something happened.
      updated.toolCalls.push({ id, name: 'tool', status: 'done', result })
    }
  }
  return updated
}

/**
 * Turn the server's raw message list (the exact shape stored by Mastra +
 * PrismaStore) into the dock's Message[]. Handles:
 *   - user messages with text + image attachments
 *   - assistant messages with text + embedded tool-call parts
 *   - role: 'tool' messages: fold their tool-result parts onto the previous
 *     assistant message's toolCalls (don't render a separate bubble)
 *   - role: 'system' messages: drop
 */
function buildMessagesFromHistory(
  raw: { id: string; role: string; content: unknown }[],
  threadId: string
): Message[] {
  const out: Message[] = []
  for (let i = 0; i < raw.length; i++) {
    const m = raw[i]
    if (m.role === 'system') continue
    if (m.role === 'tool') {
      const last = out[out.length - 1]
      if (last && last.role === 'assistant') {
        out[out.length - 1] = applyToolResults(last, m.content)
      }
      continue
    }
    const role: 'user' | 'assistant' = m.role === 'user' ? 'user' : 'assistant'
    const text = extractText(m.content)
    const attachmentUrls =
      role === 'user' ? extractAttachmentUrls(m.content) : undefined
    const toolCalls = role === 'assistant' ? extractToolCalls(m.content) : []
    out.push({
      id: m.id ?? `${threadId}-${i}`,
      role,
      text,
      toolCalls,
      attachmentUrls: attachmentUrls?.length ? attachmentUrls : undefined,
    })
  }
  return out
}

function relativeTime(iso: string): string {
  const d = new Date(iso).getTime()
  const diff = Date.now() - d
  const m = Math.round(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.round(m / 60)
  if (h < 24) return `${h}h ago`
  const day = Math.round(h / 24)
  if (day < 7) return `${day}d ago`
  return new Date(iso).toLocaleDateString()
}
